import { useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { emojiProduit, useMagasin, useRayons, type Produit } from '../lib/client/hooks.ts'
import { chercher } from '../lib/search.ts'
import { annoncer } from '../components/Toast.tsx'
import { Coche, Croix } from '../components/Icone.tsx'

type Brouillon = {
  id: string
  titre: string
  portions: string
  preparation: string
  cuisson: string
  ingredients: Array<{
    cle: string
    catalogItemId: string | null
    texte: string
    quantite: string
    facultatif: boolean
  }>
  etapes: string[]
}

/**
 * Éditeur de recette — plein écran, pas une feuille.
 *
 * Une recette, c'est un titre, dix ingrédients et six étapes : au-delà d'une
 * poignée de champs, une feuille basse devient un puits sans fond qu'on
 * scrolle en aveugle, et le clavier en mange la moitié.
 *
 * ON ÉDITE UN BROUILLON, et on n'enregistre qu'à la fin.
 *
 * C'est imposé par le modèle de données, et c'est le bon choix : une recette
 * se synchronise en DOCUMENT ENTIER (dernier arrivé gagne), parce que
 * fusionner deux listes d'étapes ordonnées produit des monstres. Émettre une
 * opération à chaque frappe ferait donc s'écraser les versions
 * intermédiaires les unes sur les autres pour rien.
 */
export function EditeurRecette({
  recetteId,
  onFermer,
}: {
  recetteId: string | 'nouvelle' | null
  onFermer: () => void
}) {
  const { etat } = useMagasin()

  const vivant = <T,>(m: Map<string, unknown>) =>
    [...m.values()].filter((l) => !(l as { deletedAt?: unknown }).deletedAt) as T[]

  const produits = useMemo(() => vivant<Produit>(etat.catalogItems), [etat])

  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)

  // Initialisation à l'ouverture, une seule fois : on ne veut PAS que la
  // synchro vienne écraser ce qu'on est en train de taper.
  if (recetteId && !brouillon) {
    if (recetteId === 'nouvelle') {
      setBrouillon({
        id: magasin.id(),
        titre: '',
        portions: '4',
        preparation: '',
        cuisson: '',
        ingredients: [ligneVide()],
        etapes: [''],
      })
    } else {
      const r = vivant<Record<string, unknown>>(etat.recipes).find((x) => x.id === recetteId)
      const ings = vivant<Record<string, unknown>>(etat.recipeIngredients)
        .filter((i) => i.recipeId === recetteId)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
      if (r) {
        setBrouillon({
          id: String(r.id),
          titre: String(r.title ?? ''),
          portions: r.servings ? String(r.servings) : '',
          preparation: r.prepMinutes ? String(r.prepMinutes) : '',
          cuisson: r.cookMinutes ? String(r.cookMinutes) : '',
          ingredients: ings.length
            ? ings.map((i) => ({
                cle: String(i.id),
                catalogItemId: (i.catalogItemId as string) ?? null,
                texte: i.catalogItemId
                  ? (produits.find((p) => p.id === i.catalogItemId)?.name ?? '')
                  : String(i.freeText ?? ''),
                quantite: String(i.quantityText ?? ''),
                facultatif: Boolean(i.optional),
              }))
            : [ligneVide()],
          etapes: ((r.steps as string[]) ?? ['']).length ? ((r.steps as string[]) ?? ['']) : [''],
        })
      }
    }
  }

  if (!recetteId || !brouillon) return null
  const b = brouillon
  const maj = (p: Partial<Brouillon>) => setBrouillon({ ...b, ...p })

  function enregistrer() {
    const titre = b.titre.trim()
    if (!titre) {
      annoncer('Il faut un titre.')
      return
    }
    const ingredients = b.ingredients
      .filter((i) => i.texte.trim())
      .map((i) => ({
        id: magasin.id(),
        // Rattaché au catalogue quand on a choisi une suggestion : c'est ce
        // rattachement qui fait fonctionner le verdict « ai-je tout ? ».
        catalogItemId: i.catalogItemId ?? undefined,
        freeText: i.catalogItemId ? undefined : i.texte.trim(),
        quantityText: i.quantite.trim() || undefined,
        optional: i.facultatif,
      }))

    magasin.dispatch('recipe.upsert', {
      id: b.id,
      title: titre,
      servings: b.portions ? Number(b.portions) : undefined,
      prepMinutes: b.preparation ? Number(b.preparation) : undefined,
      cookMinutes: b.cuisson ? Number(b.cuisson) : undefined,
      steps: b.etapes.map((e) => e.trim()).filter(Boolean),
      ingredients,
    })
    setBrouillon(null)
    onFermer()
    annoncer(`« ${titre} » enregistrée`)
  }

  function supprimer() {
    const titre = b.titre.trim() || 'La recette'
    magasin.dispatch('recipe.delete', { id: b.id })
    setBrouillon(null)
    onFermer()
    annoncer(`${titre} supprimée`, {
      label: 'Annuler',
      faire: () =>
        magasin.dispatch('recipe.upsert', {
          id: b.id,
          title: b.titre.trim(),
          servings: b.portions ? Number(b.portions) : undefined,
          prepMinutes: b.preparation ? Number(b.preparation) : undefined,
          cookMinutes: b.cuisson ? Number(b.cuisson) : undefined,
          steps: b.etapes.filter(Boolean),
          ingredients: b.ingredients
            .filter((i) => i.texte.trim())
            .map((i) => ({
              id: magasin.id(),
              catalogItemId: i.catalogItemId ?? undefined,
              freeText: i.catalogItemId ? undefined : i.texte.trim(),
              quantityText: i.quantite.trim() || undefined,
              optional: i.facultatif,
            })),
        }),
    })
  }

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-canvas" style={{ animation: 'monter 420ms var(--ease-drawer)' }}>
      <header
        className="shrink-0 border-b border-line bg-canvas"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-[var(--size-bar)] items-center justify-between px-4">
          <button
            type="button"
            onClick={() => {
              setBrouillon(null)
              onFermer()
            }}
            className="pressable text-body text-muted"
          >
            Annuler
          </button>
          <span className="text-meta text-muted">
            {recetteId === 'nouvelle' ? 'Nouvelle recette' : 'Modifier'}
          </span>
          <button type="button" onClick={enregistrer} className="pressable text-body font-semibold text-accent">
            Enregistrer
          </button>
        </div>
      </header>

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4"
        style={{ paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))' }}
      >
        <input
          value={b.titre}
          onChange={(e) => maj({ titre: e.target.value })}
          placeholder="Titre de la recette"
          autoFocus={recetteId === 'nouvelle'}
          enterKeyHint="next"
          className="mb-3 h-[52px] w-full rounded-sm bg-surface-sunk px-3 font-serif text-title text-ink outline-none"
        />

        <div className="mb-5 flex gap-2">
          <Nombre label="Personnes" valeur={b.portions} onChange={(v) => maj({ portions: v })} />
          <Nombre label="Prépa (min)" valeur={b.preparation} onChange={(v) => maj({ preparation: v })} />
          <Nombre label="Cuisson (min)" valeur={b.cuisson} onChange={(v) => maj({ cuisson: v })} />
        </div>

        <h2 className="mb-2 text-label uppercase text-muted">Ingrédients</h2>
        <p className="mb-2 text-meta text-muted">
          Choisis une suggestion pour rattacher l'ingrédient à ton inventaire — c'est ce qui fait
          marcher le « il manque N ». Le texte libre reste possible.
        </p>
        <ul className="mb-2 flex flex-col gap-2">
          {b.ingredients.map((ing, i) => (
            <LigneIngredient
              key={ing.cle}
              ing={ing}
              produits={produits}
              onChange={(patch) =>
                maj({
                  ingredients: b.ingredients.map((x, j) => (i === j ? { ...x, ...patch } : x)),
                })
              }
              onRetirer={() =>
                maj({
                  ingredients:
                    b.ingredients.length > 1
                      ? b.ingredients.filter((_, j) => j !== i)
                      : [ligneVide()],
                })
              }
            />
          ))}
        </ul>
        <button
          type="button"
          onClick={() => maj({ ingredients: [...b.ingredients, ligneVide()] })}
          className="pressable mb-6 h-11 w-full rounded-sm bg-surface-sunk text-body text-ink"
        >
          + Ingrédient
        </button>

        <h2 className="mb-2 text-label uppercase text-muted">Préparation</h2>
        <ol className="mb-2 flex flex-col gap-2">
          {b.etapes.map((e, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="tnum mt-3 w-4 shrink-0 text-meta text-muted">{i + 1}</span>
              <textarea
                value={e}
                onChange={(ev) =>
                  maj({ etapes: b.etapes.map((x, j) => (i === j ? ev.target.value : x)) })
                }
                rows={2}
                placeholder="Décris l'étape…"
                className="min-h-[52px] flex-1 resize-y rounded-sm bg-surface-sunk px-3 py-2 text-field text-ink outline-none"
              />
              <button
                type="button"
                aria-label={`Retirer l'étape ${i + 1}`}
                onClick={() =>
                  maj({ etapes: b.etapes.length > 1 ? b.etapes.filter((_, j) => j !== i) : [''] })
                }
                className="pressable mt-2 flex size-9 shrink-0 items-center justify-center text-muted"
              >
                <Croix className="size-4" />
              </button>
            </li>
          ))}
        </ol>
        <button
          type="button"
          onClick={() => maj({ etapes: [...b.etapes, ''] })}
          className="pressable mb-8 h-11 w-full rounded-sm bg-surface-sunk text-body text-ink"
        >
          + Étape
        </button>

        {recetteId !== 'nouvelle' && (
          <button
            type="button"
            onClick={supprimer}
            className="pressable h-[52px] w-full rounded-sm bg-epuise-soft text-head text-epuise-ink"
          >
            Supprimer la recette
          </button>
        )}
      </div>
    </div>
  )
}

function ligneVide() {
  return {
    cle: crypto.randomUUID(),
    catalogItemId: null,
    texte: '',
    quantite: '',
    facultatif: false,
  }
}

function Nombre({
  label,
  valeur,
  onChange,
}: {
  label: string
  valeur: string
  onChange: (v: string) => void
}) {
  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-meta text-muted">{label}</span>
      <input
        value={valeur}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        className="h-[48px] w-full rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
      />
    </label>
  )
}

function LigneIngredient({
  ing,
  produits,
  onChange,
  onRetirer,
}: {
  ing: Brouillon['ingredients'][number]
  produits: Produit[]
  onChange: (p: Partial<Brouillon['ingredients'][number]>) => void
  onRetirer: () => void
}) {
  const [focus, setFocus] = useState(false)
  const rayons = useRayons()
  const suggestions = useMemo(
    () =>
      focus && ing.texte.trim() && !ing.catalogItemId
        ? chercher(produits.map((p) => ({ ...p, nom: p.name })), ing.texte, 4)
        : [],
    [focus, ing.texte, ing.catalogItemId, produits],
  )

  return (
    <li className="rounded-sm bg-surface-sunk p-2">
      <div className="flex gap-2">
        <input
          value={ing.quantite}
          onChange={(e) => onChange({ quantite: e.target.value })}
          placeholder="200 g"
          className="h-[44px] w-[84px] shrink-0 rounded-sm bg-surface px-2 text-field text-ink outline-none"
        />
        <input
          value={ing.texte}
          onChange={(e) => onChange({ texte: e.target.value, catalogItemId: null })}
          onFocus={() => setFocus(true)}
          // Délai avant de masquer : sans lui, le clic sur une suggestion
          // arrive après le blur et la liste a déjà disparu.
          onBlur={() => setTimeout(() => setFocus(false), 180)}
          placeholder="Ingrédient"
          autoCorrect="off"
          className="h-[44px] min-w-0 flex-1 rounded-sm bg-surface px-2 text-field text-ink outline-none"
        />
        <button
          type="button"
          aria-label="Retirer l'ingrédient"
          onClick={onRetirer}
          className="pressable flex size-[44px] shrink-0 items-center justify-center text-muted"
        >
          <Croix className="size-4" />
        </button>
      </div>

      {suggestions.length > 0 && (
        <ul className="mt-1">
          {suggestions.map((p) => {
            const produit = p as unknown as Produit
            return (
              <li key={produit.id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange({ texte: produit.name, catalogItemId: produit.id })
                    setFocus(false)
                  }}
                  className="pressable flex h-10 w-full items-center gap-2 rounded-sm px-2 text-left text-body text-ink"
                >
                  <span aria-hidden>{emojiProduit(produit, rayons) ?? '·'}</span>
                  {produit.name}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-1.5 flex items-center justify-between px-1">
        <span className="flex items-center gap-1 text-meta text-muted">
          {ing.catalogItemId ? (
            <>
              <Coche className="size-3.5 text-ok" />
              rattaché à l’inventaire
            </>
          ) : ing.texte.trim() ? (
            'texte libre'
          ) : null}
        </span>
        <label className="flex items-center gap-2 text-meta text-muted">
          <input
            type="checkbox"
            checked={ing.facultatif}
            onChange={(e) => onChange({ facultatif: e.target.checked })}
            className="size-4"
          />
          facultatif
        </label>
      </div>
    </li>
  )
}
